import React from 'react';
import { CircularProgress, Typography, Box, Collapse, IconButton } from '@mui/material';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import { postApi } from "../../../utils/apiFunction";
import TextField from '@mui/material/TextField';
import { useJumboTheme } from "@jumbo/hooks";
import getCreditMemoData from '../Function/GetCreditMemoStock';
import Button from '@mui/material/Button';

const EditCreditMemoStock = (props) => {
    const [loading, setLoading] = React.useState(false);
    const [procurement, setProcurement] = React.useState([]);
    const [expanded, setExpanded] = React.useState(new Set());
    const [localReload, setLocalReload] = React.useState(false);
    // true if ANY line oldPrice != price
    const [priceChanged, setPriceChanged] = React.useState(false);
    // false where ANY child line has quantity != 0 (computed below)
    const [quantityChanged, setQuantityChanged] = React.useState(false);
    const { theme } = useJumboTheme();

    React.useEffect(() => {
        const abortController = new AbortController();
        setProcurement([]);
        console.log('Fetching credit memo data for id:' + props.status);
        getCreditMemoData(props.id, abortController, setLoading, setProcurement);
        return () => abortController.abort();
    }, [props.id, props.reload, localReload]);

    // Detect price changes whenever procurement updates
    React.useEffect(() => {
        const hasChange = procurement.some(line => {
            const oldP = line?.oldPrice;
            const newP = line?.price;
            if (oldP == null && newP == null) return false;
            return String(oldP) !== String(newP);
        });
        if (hasChange !== priceChanged) {
            setPriceChanged(hasChange);
            // eslint-disable-next-line no-console
            console.log('priceChanged set to', hasChange);
        }
    }, [procurement, priceChanged]);

    // Set quantityChanged to false where ANY child line has quantity != 0
    React.useEffect(() => {
        const anyNonZeroQty = procurement.some(line => {
            const details = line?.creditMemoLineDetails || [];
            return details.some(cl => {
                const q = cl?.quantity;
                if (q == null) return false;
                const val = typeof q === 'string' ? parseFloat(q) : Number(q);
                return !Number.isNaN(val) && val !== 0;
            });
        });

        const next = anyNonZeroQty; // false if any child has non-zero quantity
        if (next !== quantityChanged) {
            setQuantityChanged(next);
            // eslint-disable-next-line no-console
            console.log('quantityChanged set to', next);
        }
    }, [procurement, quantityChanged]);

    const triggerReload = React.useCallback(() => {
        setLocalReload(r => !r);
    }, []);

    const toggle = (key) => {
        setExpanded(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
        });
    };

    const cellStyle = {
        fontSize: '12px',
        paddingTop: '3px',
        paddingBottom: '3px',
        whiteSpace: 'nowrap',
        maxWidth: '150px',
        overflow: 'hidden'
    };

    return (
        <React.Fragment>
            {loading ? <CircularProgress size={20} /> : null}

            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell style={cellStyle} width="20%">Stock Item</TableCell>
                        <TableCell style={cellStyle} width="20%">Warehouse</TableCell>
                        <TableCell style={cellStyle} width="20%">Orig. Price</TableCell>
                        <TableCell style={cellStyle} width="20%">New Price</TableCell>
                        {props.showPriceLine ? (
                            <TableCell style={cellStyle}>&nbsp;</TableCell>
                        ) : null}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {procurement && procurement.length > 0 ? (
                        procurement.map((line, idx) => {
                            const key = line?.id ?? idx;
                            const childLines = line.creditMemoLineDetails || [];

                            return (
                                <React.Fragment key={key}>
                                    <TableRow>
                                        <TableCell style={cellStyle}>{line?.stockItem?.description ?? '-'}</TableCell>
                                        <TableCell style={cellStyle}>
                                            <Box display="inline-flex" alignItems="center" gap={1}>
                                                <span>{line?.warehouse?.description ?? '-'}</span>
                                                {/* {(props.status === 'Select Stock' || props.status === 'Awaiting Approval') && (
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        color="primary"
                                                        onClick={async (e) => {
                                                            e.stopPropagation();
                                                            const params = { Type: "Warehouse", Value: 0 };
                                                            await postApi('/CreditMemo/UpdateLineField/' + line?.id, params);
                                                            triggerReload();
                                                        }}
                                                        >
                                                        Change
                                                    </Button>
                                                )} */}
                                            </Box>
                                        </TableCell>
                                        <TableCell style={cellStyle}>{line?.oldPrice ?? '0'}</TableCell>
                                        <TextTableCell style={cellStyle} field="NewPrice" editable={!quantityChanged} status={props.status} oldValue={line?.oldPrice ?? '0'} id={line?.id} text={line?.price ?? '0'} onCommit={triggerReload} />
                                        {props.showPriceLine ? (
                                            <TableCell style={cellStyle}>&nbsp;</TableCell>
                                        ) : null}
                                    </TableRow>

                                    <TableRow>
                                        <TableCell colSpan={5} sx={{ p: 0, background: theme.palette.background.default }}>
                                            {childLines.length > 0 ? (
                                                <Table size="small">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell style={cellStyle} width="20%">&nbsp;</TableCell>
                                                            <TableCell style={cellStyle} width="20%">LotSerialNbr</TableCell>
                                                            <TableCell style={cellStyle} width="20%">Orig. Quantity</TableCell>
                                                            <TableCell style={cellStyle} width="20%">Quantity Returned</TableCell>
                                                            {props.showPriceLine ? (
                                                                <TableCell style={cellStyle}>Price</TableCell>
                                                            ) : null}
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {childLines.map((cl, cIdx) => (
                                                            <TableRow key={cl?.id ?? cIdx}>
                                                                <TableCell style={cellStyle}>&nbsp;</TableCell>
                                                                <TableCell style={cellStyle}>{cl?.lotSerialNbr ?? '-'}</TableCell>
                                                                <TableCell style={cellStyle}>{cl?.oldQuantity ?? 0}</TableCell>
                                                                <TextTableCell style={cellStyle} field="NewQuantity" editable={!priceChanged} oldValue={cl?.oldQuantity} status={props.status} id={cl?.id ?? cIdx} text={cl?.quantity ?? 0} onCommit={triggerReload} />
                                                                {props.showPriceLine ? (
                                                                    <TextTableCell style={cellStyle} field="LotNbrPrice" editable={!quantityChanged} oldValue={cl?.price} status={props.status} id={cl?.id ?? cIdx} text={cl?.price ?? 0} onCommit={triggerReload} />
                                                                ) : null}
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">
                                                    No additional line details.
                                                </Typography>
                                            )}
                                        </TableCell>
                                    </TableRow>

                                    <TableRow>
                                        <TableCell colSpan={5} sx={{ p: 0, height: '10px' }}></TableCell>
                                    </TableRow>
                                </React.Fragment>
                            );
                        })
                    ) : (
                        <TableRow>
                            <TableCell colSpan={5}>
                                <Typography variant="body2" color="text.secondary">
                                    No stock lines found.
                                </Typography>
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </React.Fragment>
    );
};

export default EditCreditMemoStock;

const TextTableCell = (props) => {
    const [selected, setSelected] = React.useState(false);
    const [text, setText] = React.useState(props.text);

    const updateValue = (newData, oldValue) => {

        // Allow empty string, digits, and one optional decimal point
        if (/^\d*\.?\d*$/.test(newData)) {
            let num = parseInt(newData)
            console.log('Num:', num, 'OldValue:', oldValue);

            if (props.field === 'NewQuantity' && num > oldValue) {
                setText(oldValue);
                const params = { Type: props.field, Value: oldValue };
                postApi('/CreditMemo/UpdateLineField/' + props.id, params)
            }
            else if (props.field === 'CalcPrice') {
                console.log('id:', props.id, 'field:', props.field, 'value:', newData);
                setText(newData);
                const params = { Type: props.field, Value: newData };
                postApi('/CreditMemo/UpdateLineField/' + props.id, params)
            } 
            else {
                setText(newData);
                const params = { Type: props.field, Value: newData };
                console.log('P2', params);
                postApi('/CreditMemo/UpdateLineField/' + props.id, params)
            }
        }
    };

    const finishediting = () => {
        setSelected(false);
        if (typeof props.onCommit === 'function') props.onCommit();
    }

    const processSelected = () => {
        if (props.status === 'Select Stock' || props.status === 'Awaiting Approval') {
            console.log('Editable:', props.editable);
            setSelected(props.editable);
        }
    }

    if (selected) {
        return (
            <TableCell {...props} style={{ maxWidth: '500px' }}>
                <TextField
                    autoFocus
                    disabled={props.disabled}
                    id="desc"
                    value={text ?? ''}
                    onChange={(event) => { updateValue(event.target.value, props.oldValue) }}
                    multiline
                    InputLabelProps={{ shrink: true }}
                    rows={4}
                    onBlur={() => finishediting()}
                />
            </TableCell>
        );
    } else {
        return <TableCell {...props} onClick={() => { processSelected() }}>{props.text}</TableCell>
    }
}
